import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
  type Row,
} from "@tanstack/react-table";
import { memo, useCallback, type ReactElement } from "react";

import { Spinner } from "@/components/spinner";
import { StableTableHeader, type ColumnMeta } from "@/components/stable-table-header";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  onRowClick?: (row: TData) => void;
  isLoading?: boolean;
  sorting?: SortingState;
  onSort?: (state: SortingState) => void;
  /**
   * Column metadata for stable header rendering.
   * When provided, headers render independently of data changes.
   * This prevents header flickering in real-time data tables.
   */
  columnMeta?: ColumnMeta[];
  /**
   * Stable render function for action buttons.
   * When provided, the "actions" column will render this instead of using flexRender.
   * This ensures action buttons are NEVER re-rendered by TanStack Table updates.
   * The function receives the row's original data and should return action buttons.
   */
  renderActions?: (rowData: TData) => React.ReactNode;
  /**
   * Stable render function for TP/SL cell with edit button.
   * When provided, the "tpsl" column will render this instead of using flexRender.
   * This ensures the edit button is NEVER re-rendered by TanStack Table updates.
   */
  renderTpSl?: (rowData: TData) => React.ReactNode;
  /**
   * Stable render function for leverage cell with edit button.
   * When provided, the "leverage" column will render this instead of using flexRender.
   * This ensures the edit button is NEVER re-rendered by TanStack Table updates.
   */
  renderLeverage?: (rowData: TData) => React.ReactNode;
  /**
   * Stable render function for margin cell with edit button.
   * When provided, the "margin" column will render this instead of using flexRender.
   * This ensures the edit button is NEVER re-rendered by TanStack Table updates.
   */
  renderMargin?: (rowData: TData) => React.ReactNode;
}

// ============================================================================
// STABLE DATA ROW - Custom memo comparison prevents unnecessary re-renders
// 
// TanStack Table creates new Row objects on every data change, which would
// cause all rows to re-render. We use a custom comparison that only checks
// the row ID, allowing individual cells to handle their own reactivity.
// 
// Price cells subscribe to Zustand store directly, so they update independently.
// Action buttons are rendered via renderActions prop - completely stable.
// ============================================================================
const MemoizedDataRow = memo(function MemoizedDataRow<TData>({
  row,
  onRowClick,
  hasRowClick,
  renderActions,
  renderTpSl,
  renderLeverage,
  renderMargin,
}: {
  row: Row<TData>;
  onRowClick?: (row: TData) => void;
  hasRowClick: boolean;
  renderActions?: (rowData: TData) => React.ReactNode;
  renderTpSl?: (rowData: TData) => React.ReactNode;
  renderLeverage?: (rowData: TData) => React.ReactNode;
  renderMargin?: (rowData: TData) => React.ReactNode;
}) {
  const handleClick = useCallback(() => {
    onRowClick?.(row.original);
  }, [onRowClick, row.original]);

  return (
    <TableRow
      data-state={row.getIsSelected() && "selected"}
      onClick={hasRowClick ? handleClick : undefined}
      className={cn("text-xs transition-none", hasRowClick && "cursor-pointer")}
    >
      {row.getVisibleCells().map((cell) => {
        let content;
        if (cell.column.id === "actions" && renderActions) {
          content = renderActions(row.original);
        } else if (cell.column.id === "tpsl" && renderTpSl) {
          content = renderTpSl(row.original);
        } else if (cell.column.id === "leverage" && renderLeverage) {
          content = renderLeverage(row.original);
        } else if (cell.column.id === "margin" && renderMargin) {
          content = renderMargin(row.original);
        } else {
          content = flexRender(cell.column.columnDef.cell, cell.getContext());
        }
        
        return (
          <TableCell key={cell.id}>
            {content}
          </TableCell>
        );
      })}
    </TableRow>
  );
}, (prevProps, nextProps) => {
  // Custom comparison - re-render if row ID OR data changes
  // We need to check if the row data has changed, not just the ID
  // This ensures that when a position is updated (same ID, different data), we re-render
  if (prevProps.row.id !== nextProps.row.id) return false; // Different row - must re-render
  if (prevProps.hasRowClick !== nextProps.hasRowClick) return false; // Click handler changed - must re-render
  
  // Same row ID - check if the underlying data has changed
  // Compare the original data object reference
  return prevProps.row.original === nextProps.row.original;
  // Note: onRowClick, renderActions, renderTpSl, renderLeverage, and renderMargin are stable via useCallback/useMemo
}) as <TData>(props: {
  row: Row<TData>;
  onRowClick?: (row: TData) => void;
  hasRowClick: boolean;
  renderActions?: (rowData: TData) => React.ReactNode;
  renderTpSl?: (rowData: TData) => React.ReactNode;
  renderLeverage?: (rowData: TData) => React.ReactNode;
  renderMargin?: (rowData: TData) => React.ReactNode;
}) => ReactElement;

export function DataTable<TData, TValue>({
  columns,
  data,
  onRowClick,
  isLoading,
  sorting,
  onSort,
  columnMeta,
  renderActions,
  renderTpSl,
  renderLeverage,
  renderMargin,
}: DataTableProps<TData, TValue>) {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: (updaterOrValue) => {
      if (!onSort) {
        return;
      }

      const isFn = typeof updaterOrValue === "function";
      if (!isFn) {
        onSort(updaterOrValue);
        return;
      }

      if (sorting) {
        const value = updaterOrValue(sorting);
        onSort(value);
      }
    },
    state: {
      sorting,
    },
    enableSortingRemoval: true,
  });

  // Memoize whether we have a row click handler (stable boolean)
  const hasRowClick = !!onRowClick;

  // Stable sort handler for StableTableHeader
  const handleColumnSort = useCallback((columnId: string) => {
    if (!onSort || !sorting) return;
    
    const existingSort = sorting.find(s => s.id === columnId);
    if (!existingSort) {
      // Not sorted -> sort ascending
      onSort([{ id: columnId, desc: false }]);
    } else if (!existingSort.desc) {
      // Ascending -> sort descending
      onSort([{ id: columnId, desc: true }]);
    } else {
      // Descending -> remove sort
      onSort([]);
    }
  }, [onSort, sorting]);

  return (
    <Table containerClassName="overflow-x-auto overflow-y-visible overscroll-x-contain scrollbar-auto-hide">
      {/* Use StableTableHeader when columnMeta is provided - headers never re-render on data changes */}
      {columnMeta ? (
        <StableTableHeader
          columnMeta={columnMeta}
          sorting={sorting || []}
          onSort={handleColumnSort}
        />
      ) : (
        /* Fallback: use TanStack Table's header rendering for tables without columnMeta */
        <TableHeader className="sticky top-0 bg-table-row z-1">
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow
              key={headerGroup.id}
              className="hover:bg-transparent transition-none text-xs"
            >
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
      )}
      <TableBody>
        {isLoading ? (
          <TableRow className="transition-none text-xs">
            <TableCell colSpan={columns.length} className="h-24 ">
              <div className="flex justify-center items-center">
                <Spinner />
              </div>
            </TableCell>
          </TableRow>
        ) : table.getRowModel().rows?.length ? (
          table.getRowModel().rows.map((row) => (
            <MemoizedDataRow<TData>
              key={row.id}
              row={row}
              onRowClick={onRowClick}
              hasRowClick={hasRowClick}
              renderActions={renderActions}
              renderTpSl={renderTpSl}
              renderLeverage={renderLeverage}
              renderMargin={renderMargin}
            />
          ))
        ) : (
          <TableRow className="transition-none text-xs">
            <TableCell colSpan={columns.length} className="h-24 text-center">
              No results.
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}
